// Query factories and mutations for the finance section. Every key lives
// under the ['finance'] root, so one invalidation after a write refreshes the
// whole section — balances, feeds and baselines all move together when money
// moves, and a page that shows a stale balance next to a fresh payment is
// worse than a page that reloads both.
import { useCallback, useEffect, useRef, useState } from 'react';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  abandonFinanceRefundAttempt,
  attachFinanceRefund,
  closeFinancePeriod,
  createFinanceAccount,
  createFinanceBankCheck,
  createFinanceCategory,
  createFinanceCounterparty,
  createFinanceExpected,
  createFinanceMovement,
  createFinanceRule,
  createFinanceSubmission,
  createFinanceTransfer,
  getFinanceAttachmentDownloadUrl,
  getFinanceExpected,
  getFinanceMovement,
  getFinancePeriod,
  getFinancePeriodPreview,
  getFinanceRule,
  getFinanceSettings,
  getFinanceSubmissionRefs,
  issueFinanceAttachmentUploadUrl,
  listFinanceAccounts,
  listFinanceBalances,
  listFinanceBankChecks,
  listFinanceCategories,
  listFinanceCounterparties,
  createFinanceEnvelope,
  listFinanceEnvelopeMoves,
  listFinanceEnvelopes,
  updateFinanceEnvelope,
  listFinanceExpected,
  listFinanceFxRates,
  listFinanceMovements,
  listFinanceOpeningBalances,
  listFinancePeriods,
  listFinanceProviderEvents,
  listFinanceQueue,
  listFinanceRefunds,
  listFinanceRules,
  listMyFinanceSubmissions,
  matchFinanceRefundAttempt,
  moveFinanceEnvelope,
  moveFinanceExpected,
  previewFinanceSettle,
  rebuildFinanceRule,
  recomputeFinanceFx,
  registerFinanceAttachment,
  reopenFinancePeriod,
  resolveFinanceProviderPayment,
  resolveFinanceProviderRefund,
  reverseFinanceEnvelopeMove,
  reverseFinanceMovement,
  sendFinanceSubmission,
  setFinanceFxRate,
  setFinanceOpeningBalance,
  settleFinanceBatch,
  settleFinanceExpected,
  settleFinanceProviderPayment,
  settleFinanceProviderRefund,
  settleFinanceScheduleEntry,
  skipFinanceExpected,
  splitFinanceExpected,
  suggestFinanceMovementEnvelope,
  splitFinanceRefund,
  unresolveFinanceProviderPayment,
  unresolveFinanceProviderRefund,
  updateFinanceAccount,
  updateFinanceCategory,
  updateFinanceCounterparty,
  updateFinanceRule,
  updateFinanceSettings,
  updateFinanceSubmission,
  writeOffFinanceRefund,
} from '@/api/generated/sdk.gen';
import type {
  CreateFinanceMovementRequest,
  CreateFinanceTransferRequest,
  FinanceAccount,
  FinanceAccountInput,
  FinanceBankCheckRequest,
  FinanceBatchRequest,
  FinanceCategory,
  FinanceCategoryInput,
  FinanceCounterpartyInput,
  FinanceEnvelopeHintRequest,
  FinanceEnvelopeInput,
  FinanceEnvelopeMoveRequest,
  FinanceExpected,
  FinanceExpectedWrite,
  FinanceOpeningBalanceRequest,
  FinanceQueueItem,
  FinanceRule,
  FinanceRuleUpdate,
  FinanceRuleWrite,
  FinanceSettlePreviewRequest,
  FinanceSettlePreviewResponse,
  FinanceSettleRequest,
  FinanceSubmissionWrite,
  RecomputeFinanceFxRequest,
  ReverseFinanceEnvelopeMoveRequest,
  ReverseFinanceMovementRequest,
  UpdateFinanceSettingsRequest,
} from '@/api/generated/types.gen';
import { errorCode, errorMessage } from '@/lib/errors';
import type { FinanceEnvelopeHint } from '@/lib/envelopeTag';
import { scheduleKey } from '@/lib/payments';
import type { DraftContext } from '@/lib/settleDraft';

const root = ['finance'] as const;

/**
 * The movements feed's filter. `limit`/`offset` are required because the
 * server's schema bounds them (1…200, ≥0) and a page that forgets them gets
 * the server's default rather than the one the Previous/Next buttons count on.
 */
export interface MovementFilter {
  accountId?: string;
  currency?: string;
  from?: string;
  to?: string;
  kind?: 'payment' | 'transfer';
  direction?: 'in' | 'out';
  apartmentId?: string;
  bookingId?: string;
  categoryId?: string;
  includeReversed?: boolean;
  limit: number;
  offset: number;
}

export const financeAccountsQuery = (includeInactive = false) =>
  queryOptions({
    queryKey: [...root, 'accounts', includeInactive],
    queryFn: async ({ signal }) =>
      (
        await listFinanceAccounts({
          throwOnError: true,
          signal,
          query: { include_inactive: includeInactive },
        })
      ).data,
    staleTime: 30_000,
  });

/**
 * Balances on a date. `asOf` omitted sends no as_of at all: the server then
 * answers for today in the BUSINESS timezone. Computing "today" in the
 * browser would be a day out for anyone whose zone has already rolled over,
 * and every returned row carries the as_of the server actually used, so the
 * page can show the date it got rather than the date it guessed.
 */
export const financeBalancesQuery = (asOf?: string) =>
  queryOptions({
    queryKey: [...root, 'balances', asOf ?? 'today'],
    queryFn: async ({ signal }) =>
      (
        await listFinanceBalances({
          throwOnError: true,
          signal,
          query: asOf ? { as_of: asOf } : {},
        })
      ).data,
    staleTime: 15_000,
  });

export const financeOpeningBalancesQuery = (accountId: string) =>
  queryOptions({
    queryKey: [...root, 'accounts', accountId, 'opening'],
    queryFn: async ({ signal }) =>
      (await listFinanceOpeningBalances({ throwOnError: true, signal, path: { id: accountId } }))
        .data,
  });

export const financeMovementsQuery = (f: MovementFilter) =>
  queryOptions({
    queryKey: [...root, 'movements', f],
    queryFn: async ({ signal }) =>
      (
        await listFinanceMovements({
          throwOnError: true,
          signal,
          query: {
            account_id: f.accountId,
            currency: f.currency,
            from: f.from,
            to: f.to,
            kind: f.kind,
            direction: f.direction,
            apartment_id: f.apartmentId,
            booking_id: f.bookingId,
            category_id: f.categoryId,
            include_reversed: f.includeReversed,
            limit: f.limit,
            offset: f.offset,
          },
        })
      ).data,
    staleTime: 15_000,
  });

export const financeMovementQuery = (id: string) =>
  queryOptions({
    queryKey: [...root, 'movements', 'one', id],
    queryFn: async ({ signal }) =>
      (await getFinanceMovement({ throwOnError: true, signal, path: { id } })).data,
  });

export const financeCategoriesQuery = (includeInactive = false) =>
  queryOptions({
    queryKey: [...root, 'categories', includeInactive],
    queryFn: async ({ signal }) =>
      (
        await listFinanceCategories({
          throwOnError: true,
          signal,
          query: { include_inactive: includeInactive },
        })
      ).data,
    staleTime: 60_000,
  });

export const financeCounterpartiesQuery = (includeInactive = false) =>
  queryOptions({
    queryKey: [...root, 'counterparties', includeInactive],
    queryFn: async ({ signal }) =>
      (
        await listFinanceCounterparties({
          throwOnError: true,
          signal,
          query: { include_inactive: includeInactive },
        })
      ).data,
    staleTime: 60_000,
  });

export const financeSettingsQuery = queryOptions({
  queryKey: [...root, 'settings'],
  queryFn: async ({ signal }) => (await getFinanceSettings({ throwOnError: true, signal })).data,
});

/**
 * Envelopes with balances and operating money (spec 2026-10-21 §2, §7).
 * `availableOn` adds available_eur_minor per active envelope — what one spend
 * dated that day can take (the server's own §4 engine).
 */
export const financeEnvelopesQuery = (
  opts: { includeInactive?: boolean; availableOn?: string } = {},
) =>
  queryOptions({
    queryKey: [...root, 'envelopes', opts.includeInactive ?? false, opts.availableOn ?? null] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceEnvelopes({
          throwOnError: true,
          signal,
          query: { include_inactive: opts.includeInactive, available_on: opts.availableOn },
        })
      ).data,
  });

/**
 * Spec rev. 4 §8 (EP-05): the server's envelope suggestion for the payment
 * form's current state. The settings below only spare background requests: a
 * later answer for the same input comes from a finance write invalidating
 * ['finance'], not from a focus or a reconnect. An automatic tag's amount is
 * protected by the draft (EP-R2-02, envelopeTag.reduceDraft), which never
 * re-applies such an answer to it (it offers «Apply»); a manual tag or an
 * explicit None is offered nothing and converts at the newest answer for its input.
 */
export const financeEnvelopeHintQuery = (body: FinanceEnvelopeHintRequest | null) =>
  queryOptions({
    queryKey: [...root, 'envelope-hint', body] as const,
    queryFn: async ({ signal }): Promise<FinanceEnvelopeHint> => {
      if (!body) throw new Error('envelope hint: no input');
      // The generated type drops the nullable arms the schema declares.
      return (await suggestFinanceMovementEnvelope({ throwOnError: true, signal, body }))
        .data as FinanceEnvelopeHint;
    },
    enabled: body !== null,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const financeEnvelopeMovesQuery = (envelopeId?: string, limit = 50, offset = 0) =>
  queryOptions({
    queryKey: [...root, 'envelope-moves', envelopeId ?? 'all', limit, offset] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceEnvelopeMoves({
          throwOnError: true,
          signal,
          query: { envelope_id: envelopeId, limit, offset },
        })
      ).data,
  });

/** Both bounds are required by the server — the query has no open-ended form. */
export const financeFxRatesQuery = (from: string, to: string, currency?: string) =>
  queryOptions({
    queryKey: [...root, 'fx', from, to, currency ?? 'all'],
    queryFn: async ({ signal }) =>
      (await listFinanceFxRates({ throwOnError: true, signal, query: { from, to, currency } })).data,
  });

export const financeBankChecksQuery = (accountId?: string, limit = 50, offset = 0) =>
  queryOptions({
    queryKey: [...root, 'bank-checks', accountId ?? 'all', limit, offset],
    queryFn: async ({ signal }) =>
      (
        await listFinanceBankChecks({
          throwOnError: true,
          signal,
          query: { account_id: accountId, limit, offset },
        })
      ).data,
  });

export const financePeriodsQuery = queryOptions({
  queryKey: [...root, 'periods'],
  queryFn: async ({ signal }) => (await listFinancePeriods({ throwOnError: true, signal })).data,
});

function useFinanceMutation<TVars, TData>(fn: (v: TVars) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: root });
    },
  });
}

export const useCreateFinanceMovement = () =>
  useFinanceMutation((body: CreateFinanceMovementRequest) =>
    createFinanceMovement({ throwOnError: true, body }).then((r) => r.data),
  );
export const useReverseFinanceMovement = () =>
  useFinanceMutation(({ id, body }: { id: string; body: ReverseFinanceMovementRequest }) =>
    reverseFinanceMovement({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useCreateFinanceTransfer = () =>
  useFinanceMutation((body: CreateFinanceTransferRequest) =>
    createFinanceTransfer({ throwOnError: true, body }).then((r) => r.data),
  );
export const useCreateFinanceEnvelope = () =>
  useFinanceMutation((body: FinanceEnvelopeInput) =>
    createFinanceEnvelope({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceEnvelope = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceEnvelopeInput }) =>
    updateFinanceEnvelope({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useMoveFinanceEnvelope = () =>
  useFinanceMutation((body: FinanceEnvelopeMoveRequest) =>
    moveFinanceEnvelope({ throwOnError: true, body }).then((r) => r.data),
  );
export const useReverseFinanceEnvelopeMove = () =>
  useFinanceMutation(({ id, body }: { id: string; body: ReverseFinanceEnvelopeMoveRequest }) =>
    reverseFinanceEnvelopeMove({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useSetFinanceOpeningBalance = () =>
  useFinanceMutation(({ accountId, body }: { accountId: string; body: FinanceOpeningBalanceRequest }) =>
    setFinanceOpeningBalance({ throwOnError: true, path: { id: accountId }, body }).then(
      (r) => r.data,
    ),
  );
export const useCreateFinanceBankCheck = () =>
  useFinanceMutation((body: FinanceBankCheckRequest) =>
    createFinanceBankCheck({ throwOnError: true, body }).then((r) => r.data),
  );
export const useCreateFinanceAccount = () =>
  useFinanceMutation((body: FinanceAccountInput) =>
    createFinanceAccount({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceAccount = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceAccountInput }) =>
    updateFinanceAccount({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useCreateFinanceCategory = () =>
  useFinanceMutation((body: FinanceCategoryInput) =>
    createFinanceCategory({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceCategory = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceCategoryInput }) =>
    updateFinanceCategory({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useCreateFinanceCounterparty = () =>
  useFinanceMutation((body: FinanceCounterpartyInput) =>
    createFinanceCounterparty({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceCounterparty = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceCounterpartyInput }) =>
    updateFinanceCounterparty({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useUpdateFinanceSettings = () =>
  useFinanceMutation((body: UpdateFinanceSettingsRequest) =>
    updateFinanceSettings({ throwOnError: true, body }).then((r) => r.data),
  );
export const useSetFinanceFxRate = () =>
  useFinanceMutation(({ date, currency, rateMicro }: { date: string; currency: string; rateMicro: number }) =>
    setFinanceFxRate({
      throwOnError: true,
      path: { date, currency },
      body: { rate_micro: rateMicro },
    }).then((r) => r.data),
  );
export const useRecomputeFinanceFx = () =>
  useFinanceMutation((body: RecomputeFinanceFxRequest) =>
    recomputeFinanceFx({ throwOnError: true, body }).then((r) => r.data),
  );

/**
 * One operation_id per opened form, reused on retry: the server replays the
 * stored result instead of writing the payment twice (§5.19). A fresh id per
 * attempt would turn a timeout into a double payment.
 */
export function newOperationId(): string {
  return crypto.randomUUID();
}

/**
 * The one helper for the operation_id lifecycle (contract §9), so slice-2
 * forms don't each invent their own `useState(newOperationId)`. Mints an id
 * once and reuses it across re-renders and repeated submits of the SAME
 * form — a retried request replays instead of paying twice.
 *
 * `formKey` names which logical "open" this is. A dialog that fully
 * unmounts on close and remounts on open (`{open && <Dialog/>}`, the
 * pattern every slice-1 form uses) gets a fresh id for free from useState's
 * lazy initializer and can leave `formKey` out. A component that stays
 * mounted across different targets — one dialog instance reused for every
 * row of a table, toggled by an `item` prop rather than by
 * mounting/unmounting — MUST pass the target's own id as `formKey`, or
 * every open after the first replays the FIRST open's key against a
 * DIFFERENT target. When `formKey` changes, a fresh id is minted
 * synchronously (the "adjust state during render when a prop changes"
 * pattern: https://react.dev/learn/you-might-not-need-an-effect), so no
 * stale render can slip out with the previous open's id. The returned
 * `renew` is for the remaining case — a form that neither unmounts nor
 * changes target (the phone submission page, reused for a second expense
 * after a successful send): call it once the form is done with its id.
 */
export function useOperationId(formKey?: unknown): [string, () => void] {
  const [state, setState] = useState(() => ({ key: formKey, id: newOperationId() }));
  const renew = useCallback(() => setState((s) => ({ key: s.key, id: newOperationId() })), []);
  if (state.key !== formKey) {
    const id = newOperationId();
    setState({ key: formKey, id });
    return [id, renew];
  }
  return [state.id, renew];
}

/**
 * Today as the YYYY-MM-DD a <input type="date"> wants. `tz` is the business
 * timezone from finance settings: without it the browser's own zone decides,
 * and at 23:30 in Belgrade a UTC-based answer is yesterday — a payment dated
 * into the previous month, possibly a closed one. en-CA is used because it
 * formats a date as YYYY-MM-DD; `now` is injectable so the rule is testable.
 */
export function todayInput(tz?: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', tz ? { timeZone: tz } : {}).format(now);
}

/**
 * A confirmation's default date (OH-201, owner 2026-10-23): the day the
 * payment was planned for, once that day has come — a payment confirmed ahead
 * of its plan moved today. `planned` is a date or a timestamp; a timestamp is
 * read as a day in the business timezone, like todayInput.
 */
export function plannedOrToday(planned: string | null | undefined, tz?: string, now: Date = new Date()): string {
  const today = todayInput(tz, now);
  if (!planned) return today;
  let day = planned;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(planned)) {
    const at = new Date(planned);
    if (Number.isNaN(at.getTime())) return today;
    day = todayInput(tz, at);
  }
  return day <= today ? day : today;
}

/**
 * The account a confirmation starts on (OH-203, owner 2026-10-23): the one the
 * payment is set up to go through — its rule's default account, else its
 * category's — as long as that account is active; else the first active
 * account holding the payment's currency, then the old fallbacks. The operator
 * can always pick another; this only stops a default from being ignored.
 */
export function preferredSettleAccount(o: {
  accounts: readonly FinanceAccount[];
  currency: string;
  ruleDefault?: string | null;
  categoryDefault?: string | null;
}): FinanceAccount | undefined {
  const active = (id?: string | null) => (id ? o.accounts.find((a) => a.id === id && a.is_active) : undefined);
  return (
    active(o.ruleDefault) ??
    active(o.categoryDefault) ??
    o.accounts.find((a) => a.is_active && a.currencies.includes(o.currency)) ??
    o.accounts.find((a) => a.currencies.includes(o.currency)) ??
    o.accounts[0]
  );
}

/** A queue row's configured account: its rule's default, else its category's (OH-203). */
export function queueItemDefaultAccount(
  item: Pick<FinanceQueueItem, 'category_id' | 'expected'>,
  rules: readonly Pick<FinanceRule, 'id' | 'default_account_id'>[],
  categories: readonly Pick<FinanceCategory, 'id' | 'default_account_id'>[],
): { ruleDefault?: string; categoryDefault?: string } {
  const ruleId = item.expected?.rule_id;
  return {
    ruleDefault: ruleId ? rules.find((r) => r.id === ruleId)?.default_account_id : undefined,
    categoryDefault: categories.find((c) => c.id === item.category_id)?.default_account_id,
  };
}

/**
 * Σ of a movement's lines as the SERVER computes it (equation.go):
 * settle + income + expense − withheld. A withheld line is money the channel
 * kept, so it lowers what hit the bank; summing it with a plus sign would
 * refuse the ordinary Airbnb payment (income 100, withheld 15, bank 85).
 *
 * `settle` carries +1, like income and expense — lineSigns (equation.go) is
 * the authority. It reaches this function from one caller only: the
 * replacement of a reversed settlement, whose settle line(s) MovementForm
 * repeats (§8.6 R15). Left out, the Σ gate would report the whole settled
 * amount as a gap and refuse to submit a replacement the server accepts.
 */
export function lineSum(
  lines: readonly { kind: 'settle' | 'income' | 'expense' | 'withheld'; minor: number }[],
): number {
  return lines.reduce((acc, l) => acc + (l.kind === 'withheld' ? -l.minor : l.minor), 0);
}

// ---------------------------------------------------------------------
// Slice 2: queue, expected payments, rules, phone submissions, receipts.
// Same root, same invalidation-by-prefix shape as slice 1 above.
// ---------------------------------------------------------------------

/**
 * The confirmation queue's filter (spec §8.1). `channel` filters on the
 * linked booking's source; a row with no booking never matches it — it is
 * NOT the same axis as `direction` (which is money in/out). limit/offset
 * are required for the same reason MovementFilter's are (the server bounds
 * them and a page that omits them gets the server's default instead of the
 * one the Previous/Next buttons count on).
 */
export interface QueueFilter {
  direction?: 'in' | 'out';
  apartmentId?: string;
  categoryId?: string;
  origin?: 'rule' | 'operator' | 'submission';
  channel?: 'direct' | 'booking_com' | 'airbnb' | 'expedia' | 'manual';
  /**
   * Narrows to one of the three sources the queue unions (OH-166 task 19,
   * Ruling PF3-21) — slice 3's own addition to this filter. Omitted — all
   * three, same as the server's own default.
   */
  source?: 'expected' | 'schedule' | 'provider';
  dueFrom?: string;
  dueTo?: string;
  limit: number;
  offset: number;
}

function normaliseQueueFilter(f: QueueFilter) {
  return {
    direction: f.direction,
    apartment_id: f.apartmentId,
    category_id: f.categoryId,
    origin: f.origin,
    channel: f.channel,
    source: f.source,
    due_from: f.dueFrom,
    due_to: f.dueTo,
    limit: f.limit,
    offset: f.offset,
  };
}

export const financeQueueQuery = (f: QueueFilter) => {
  const q = normaliseQueueFilter(f);
  return queryOptions({
    queryKey: [...root, 'queue', q] as const,
    queryFn: async ({ signal }) =>
      (await listFinanceQueue({ throwOnError: true, signal, query: q })).data,
    staleTime: 15_000,
  });
};

export const financeExpectedQuery = (id: string) =>
  queryOptions({
    queryKey: [...root, 'expected', id] as const,
    queryFn: async ({ signal }) =>
      (await getFinanceExpected({ throwOnError: true, signal, path: { id } })).data,
  });

/** Rules list filter (spec §5.6/§7). Disabled rules are left out unless requested. */
export const financeRulesQuery = (f: {
  type?: 'recurring' | 'per_stay' | 'commission_invoice';
  apartmentId?: string;
  includeDisabled?: boolean;
}) => {
  const q = { type: f.type, apartment_id: f.apartmentId, include_disabled: f.includeDisabled ?? false };
  return queryOptions({
    queryKey: [...root, 'rules', q] as const,
    queryFn: async ({ signal }) => (await listFinanceRules({ throwOnError: true, signal, query: q })).data,
    staleTime: 30_000,
  });
};

export const financeRuleQuery = (id: string) =>
  queryOptions({
    queryKey: [...root, 'rules', 'one', id] as const,
    queryFn: async ({ signal }) => (await getFinanceRule({ throwOnError: true, signal, path: { id } })).data,
  });

/**
 * Every finance_expected row for one booking (spec §14's "Stay expenses" on
 * the booking card) — history, not a to-do list, so confirmed/skipped/
 * cancelled/draft rows are included alongside planned ones.
 */
export const financeExpectedByBookingQuery = (bookingId: string) =>
  queryOptions({
    queryKey: [...root, 'expected', 'by-booking', bookingId] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceExpected({ throwOnError: true, signal, query: { booking_id: bookingId } })
      ).data,
  });

/** financeExpectedByBookingQuery's twin for the apartment Finance tab. */
export const financeExpectedByApartmentQuery = (apartmentId: string, status?: FinanceExpected['status']) =>
  queryOptions({
    queryKey: [...root, 'expected', 'by-apartment', apartmentId, status ?? 'all'] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceExpected({
          throwOnError: true,
          signal,
          query: { apartment_id: apartmentId, status },
        })
      ).data,
  });

/**
 * financeExpectedByBookingQuery's twin for a rule's own recent-occurrences
 * list (finance.rules.$id.tsx).
 */
export const financeExpectedByRuleQuery = (ruleId: string, status?: FinanceExpected['status']) =>
  queryOptions({
    queryKey: [...root, 'expected', 'by-rule', ruleId, status ?? 'all'] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceExpected({ throwOnError: true, signal, query: { rule_id: ruleId, status } })
      ).data,
  });

/**
 * The phone submission form's reference data (categories, apartments, the
 * caller's own past counterparties — spec §8.3/§21). Rarely changes within
 * a session, so a longer staleTime avoids re-fetching it on every mount of
 * the submit page.
 */
export const financeSubmissionRefsQuery = queryOptions({
  queryKey: [...root, 'submissions', 'refs'] as const,
  queryFn: async ({ signal }) => (await getFinanceSubmissionRefs({ throwOnError: true, signal })).data,
  staleTime: 5 * 60_000,
});

/** The caller's own submissions, draft or sent (spec §16 item 12 — never anyone else's). */
export const financeMySubmissionsQuery = (page: { limit: number; offset: number }) =>
  queryOptions({
    queryKey: [...root, 'submissions', 'mine', page] as const,
    queryFn: async ({ signal }) =>
      (await listMyFinanceSubmissions({ throwOnError: true, signal, query: page })).data,
  });

export const useSettleFinanceExpected = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceSettleRequest }) =>
    settleFinanceExpected({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useSettleFinanceBatch = () =>
  useFinanceMutation((body: FinanceBatchRequest) =>
    settleFinanceBatch({ throwOnError: true, body }).then((r) => r.data),
  );
export const useSkipFinanceExpected = () =>
  useFinanceMutation(({ id, reason }: { id: string; reason: string }) =>
    skipFinanceExpected({ throwOnError: true, path: { id }, body: { reason } }).then((r) => r.data),
  );
export const useSplitFinanceExpected = () =>
  useFinanceMutation(({ id, operationId, parts }: { id: string; operationId: string; parts: number[] }) =>
    splitFinanceExpected({
      throwOnError: true,
      path: { id },
      body: { operation_id: operationId, parts },
    }).then((r) => r.data),
  );
export const useMoveFinanceExpected = () =>
  useFinanceMutation(({ id, dueDate }: { id: string; dueDate: string }) =>
    moveFinanceExpected({ throwOnError: true, path: { id }, body: { due_date: dueDate } }).then(
      (r) => r.data,
    ),
  );
export const useCreateFinanceExpected = () =>
  useFinanceMutation((body: FinanceExpectedWrite) =>
    createFinanceExpected({ throwOnError: true, body }).then((r) => r.data),
  );
export const useCreateFinanceRule = () =>
  useFinanceMutation((body: FinanceRuleWrite) =>
    createFinanceRule({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceRule = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceRuleUpdate }) =>
    updateFinanceRule({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
/**
 * Re-walk every booking a per_stay rule could apply to (spec §14's Rebuild
 * button). Counters (created/updated/cancelled/revived, plus bookings
 * walked) are the whole point of the response — the caller shows them to
 * the operator rather than a bare "done", since a company-scoped or
 * non-per_stay rule answers all zeros (documented no-op) and an operator
 * who cannot tell that apart from "nothing needed doing" has no way to
 * notice a rebuild that silently did not apply to the rule they picked.
 */
export const useRebuildFinanceRule = () =>
  useFinanceMutation((id: string) => rebuildFinanceRule({ throwOnError: true, path: { id } }).then((r) => r.data));
export const useCreateFinanceSubmission = () =>
  useFinanceMutation((body: FinanceSubmissionWrite) =>
    createFinanceSubmission({ throwOnError: true, body }).then((r) => r.data),
  );
export const useUpdateFinanceSubmission = () =>
  useFinanceMutation(({ id, body }: { id: string; body: FinanceSubmissionWrite }) =>
    updateFinanceSubmission({ throwOnError: true, path: { id }, body }).then((r) => r.data),
  );
export const useSendFinanceSubmission = () =>
  useFinanceMutation((id: string) =>
    sendFinanceSubmission({ throwOnError: true, path: { id } }).then((r) => r.data),
  );

const RECEIPT_MIME = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
type ReceiptMime = (typeof RECEIPT_MIME)[number];
function isReceiptMime(mime: string): mime is ReceiptMime {
  return (RECEIPT_MIME as readonly string[]).includes(mime);
}

/**
 * Exactly one target — mirrors IssueFinanceAttachmentUploadUrlRequest's
 * "exactly one of expected_id, movement_id, submission_id must be set,
 * except for a finance.confirm actor, who may also leave all three unset"
 * (openapi.yaml, spec §14, Ruling T17-4). The fourth (targetless) variant is
 * for exactly that exception — a receipt staged before the movement that
 * will own it exists yet (a schedule-only batch, spec example A's own bank
 * statement), bound to it at settle time instead of at upload time. Every
 * caller of this function today (ReceiptUploadButton, BatchSettle) already
 * only runs under finance.confirm or finance.submit's own ordinary targets,
 * so this file does not re-derive the actor's permission — a submitter
 * passing `{}` would simply get the same 422 the server already gives a
 * confirmer who both under- and over-specifies a target.
 *
 * Each named-target variant explicitly forbids the other two keys
 * (`?: never`), not just omits them: on a plain union without that,
 * TypeScript's excess property check only rejects a key absent from EVERY
 * member, so `{ expected_id, movement_id }` would satisfy the `expected_id`
 * member and compile anyway — exactly the "exactly one" this type exists to
 * enforce. The targetless variant is the same shape with every key
 * forbidden.
 */
export type ReceiptTarget =
  | { submission_id: string; expected_id?: never; movement_id?: never }
  | { expected_id: string; submission_id?: never; movement_id?: never }
  | { movement_id: string; submission_id?: never; expected_id?: never }
  | { submission_id?: never; expected_id?: never; movement_id?: never };

// uploadFinanceReceipt: presign → PUT straight to R2 (no SDK client: the PUT
// must not carry our CSRF header or cookies, same as ApartmentPhotos) →
// register — EXCEPT for a targetless upload (Ruling T17-4), which skips the
// register call entirely: RegisterFinanceAttachment's own UPDATE never
// touches movement_id/expected_id, so registering a targetless row through
// it would trip finance_attachments_one_target the instant registered_at is
// set (num_nonnulls(movement_id, expected_id) = 1), so the server refuses
// it up front with 422 (fix round 4). A targetless row stays
// unregistered on purpose until the settle call that ends up using its id
// registers AND binds it to the new movement in one statement
// (RegisterAndBindFinanceAttachmentToMovement, server-side) — the id itself
// is still usable the moment the PUT succeeds, same as before.
export async function uploadFinanceReceipt(file: File, target: ReceiptTarget): Promise<string> {
  if (!isReceiptMime(file.type)) {
    throw new Error(`Unsupported file type "${file.type || 'unknown'}" — use JPEG, PNG, WEBP or PDF.`);
  }
  const issued = await issueFinanceAttachmentUploadUrl({
    throwOnError: true,
    body: { mime: file.type, size_bytes: file.size, original_name: file.name, ...target },
  });
  const url = issued.data.upload_url.toString();
  const headers: Record<string, string> = { ...issued.data.headers };
  const putRes = await fetch(url, { method: 'PUT', headers, body: file });
  if (!putRes.ok) throw new Error(`R2 upload failed: HTTP ${putRes.status}`);
  const targetless = target.expected_id === undefined && target.movement_id === undefined && target.submission_id === undefined;
  if (!targetless) {
    await registerFinanceAttachment({ throwOnError: true, path: { id: issued.data.attachment_id } });
  }
  return issued.data.attachment_id;
}

export async function financeAttachmentDownloadUrl(id: string): Promise<string> {
  const res = await getFinanceAttachmentDownloadUrl({ throwOnError: true, path: { id } });
  return res.data.url.toString();
}

// ---------------------------------------------------------------------
// Slice 3 (task 23): the provider-events screen, month close/reopen, and
// the settle/resolve/refund mutations the booking card's "Confirm receipt"
// and the events screen both drive. Same root, same invalidation-by-prefix
// shape as slices 1–2 above — a settle here can move a schedule row, a
// provider fact, balances and the queue all at once, so one prefix
// invalidation refreshes all of them together.
// ---------------------------------------------------------------------

/** listFinanceProviderEvents' section filter (api/openapi.yaml). */
export type ProviderEventsKind =
  | 'all'
  | 'provider_queue'
  | 'unpaired'
  | 'contradictions'
  | 'stale'
  | 'anomalies';

/**
 * listFinanceProviderEvents' `processed` filter (D-I2, final review, Ruling
 * FR-1): 'none' is the queue as it always was; 'resolved' answers the SAME
 * shape but only ever carries provider_queue facts marked resolved — every
 * other section (unpaired/contradictions/stale/anomalies) comes back empty
 * (PART 1's own contract). Without this filter the events screen could
 * never show a resolved fact at all, so "Unresolve" was unreachable UI.
 */
export const financeProviderEventsQuery = (
  kind: ProviderEventsKind = 'all',
  limit = 200,
  offset = 0,
  processed: 'none' | 'resolved' = 'none',
) =>
  queryOptions({
    queryKey: [...root, 'provider-events', kind, limit, offset, processed] as const,
    queryFn: async ({ signal }) =>
      (await listFinanceProviderEvents({ throwOnError: true, signal, query: { kind, limit, offset, processed } }))
        .data,
    staleTime: 15_000,
  });

export const financePeriodQuery = (month: string) =>
  queryOptions({
    queryKey: [...root, 'periods', month] as const,
    queryFn: async ({ signal }) => (await getFinancePeriod({ throwOnError: true, signal, path: { month } })).data,
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 404 && count < 1;
    },
  });

/**
 * Spec 2026-10-21 §5.4: the advisory summary of a month that is not closed —
 * its would-be snapshot and what blocks the close. A closed month answers 409
 * period_closed (its own snapshot lives on financePeriodQuery), not retried.
 */
export const financePeriodPreviewQuery = (month: string) =>
  queryOptions({
    queryKey: [...root, 'periods', month, 'preview'] as const,
    queryFn: async ({ signal }) =>
      (await getFinancePeriodPreview({ throwOnError: true, signal, path: { month } })).data,
    retry: false,
  });

/**
 * Every slice-3 mutation below invalidates the whole ['finance'] prefix
 * (same as useFinanceMutation) PLUS, when the caller knows which booking it
 * touched, that booking's own payment-schedule query — the ONE key outside
 * the finance root a schedule-entry settle also changes (payments.ts's
 * scheduleKey, imported rather than re-typed so the two can never drift).
 * bookingId is unknown to the provider-events/period mutations (they act on
 * facts/months, not a specific booking), so they simply omit it.
 *
 * Exported (fix round 2): a screen handling an `operation_conflict` itself
 * (settleStableOperation, below) needs to invalidate manually — that error
 * path has no hook-level onSuccess to do it for them.
 */
export function useInvalidateFinance() {
  const qc = useQueryClient();
  return (bookingId?: string) => {
    void qc.invalidateQueries({ queryKey: root });
    if (bookingId) void qc.invalidateQueries({ queryKey: scheduleKey(bookingId) });
  };
}

/** The query-key prefix of every settle preview — refetchAfterConflict invalidates it. */
export const settlePreviewKey = [...root, 'settle-preview'] as const;

/** What a settle preview answered, with the request it answers (spec §4.4 p.2). */
export interface SettlePreviewAnswer {
  response: FinanceSettlePreviewResponse;
  context: DraftContext;
  sentBank: boolean;
}

/**
 * The settle preview (OH-193 spec §4.1–§4.4). Read-only on the server, so it
 * is a query, not a mutation.
 *
 * The request and the draft context it answers travel together: a response
 * is dispatched with the context of ITS request, never with the current one
 * (S1). sentBank travels with them too [PR3-01]: the cache key differs with
 * and without the bank amount, and an answer is applied only to the request
 * shape the form is in now. sentBank is the form's request SHAPE (fee mode /
 * batch), passed explicitly — not inferred from bank_amount_minor, which is
 * absent while the bank is still the suggested (non-manual) value.
 *
 * Debounced 300 ms (typing a bank amount in the batch form), except the very
 * first request: nothing to collapse there, and a dialog opening on accounts
 * that load asynchronously would otherwise wait for nothing. No
 * placeholderData: when the key changes, data must go undefined until the
 * new answer, or the previous answer would be dispatched again under the
 * new key and slip past S1. gcTime 0: an answer nobody observes is dropped,
 * so a remount never starts from a cached answer to an older state.
 */
export function useSettlePreview(input: { req: FinanceSettlePreviewRequest; context: DraftContext; sentBank: boolean } | null) {
  const inputKey = JSON.stringify(input);
  const [debounced, setDebounced] = useState(input);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input), debounced === null ? 0 : 300);
    return () => clearTimeout(t);
    // keyed on the input's VALUE: a caller building it inline must not restart the timer every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputKey]);
  return useQuery({
    queryKey: [...settlePreviewKey, debounced?.req, debounced?.sentBank],
    enabled: !!debounced,
    queryFn: async ({ signal }): Promise<SettlePreviewAnswer> => {
      const d = debounced as NonNullable<typeof debounced>;
      const { data } = await previewFinanceSettle({ throwOnError: true, signal, body: d.req });
      return { response: data, context: d.context, sentBank: d.sentBank };
    },
    staleTime: 0,
    // Never keep an answer once nobody watches it: a dialog reopened on the same
    // row must not be filled from a minutes-old answer (useSettleDraft also
    // ignores answers older than its own mount).
    gcTime: 0,
    retry: false,
  });
}

/** Settle a booking's own payment-schedule row (the "Confirm receipt" dialog). */
export function useSettleScheduleEntry(bookingId?: string) {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ entryId, body }: { entryId: string; body: FinanceSettleRequest }) =>
      settleFinanceScheduleEntry({ throwOnError: true, path: { id: entryId }, body }).then((r) => r.data),
    onSuccess: () => invalidate(bookingId),
  });
}

/** Settle a third-source provider fact directly (the events screen's own Settle action). */
export function useSettleProviderFact() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ kind, id, body }: { kind: 'payment' | 'refund'; id: string; body: FinanceSettleRequest }) =>
      (kind === 'payment'
        ? settleFinanceProviderPayment({ throwOnError: true, path: { id }, body })
        : settleFinanceProviderRefund({ throwOnError: true, path: { id }, body })
      ).then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/**
 * Resolve a provider fact without settling it (spec §9 — e.g. it will never
 * hit the bank). `operationId` is a required caller-supplied param (fix
 * round 1, item 3/T23-3) — this used to mint newOperationId() INSIDE
 * mutationFn, so every `.mutate()` call (including a retry of the very same
 * logical action after a failed request) got a fresh id, defeating the
 * idempotency contract exactly the case it exists for. The caller now owns
 * the id's lifetime (typically via useStableOperationIds, below).
 */
export function useResolveProviderFact() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({
      kind,
      id,
      resolution,
      operationId,
    }: {
      kind: 'payment' | 'refund';
      id: string;
      resolution: string;
      operationId: string;
    }) => {
      const body = { operation_id: operationId, resolution };
      return (kind === 'payment'
        ? resolveFinanceProviderPayment({ throwOnError: true, path: { id }, body })
        : resolveFinanceProviderRefund({ throwOnError: true, path: { id }, body })
      ).then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/** Same operationId-is-a-param fix as useResolveProviderFact, same reasoning. */
export function useUnresolveProviderFact() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ kind, id, operationId }: { kind: 'payment' | 'refund'; id: string; operationId: string }) => {
      const body = { operation_id: operationId };
      return (kind === 'payment'
        ? unresolveFinanceProviderPayment({ throwOnError: true, path: { id }, body })
        : unresolveFinanceProviderRefund({ throwOnError: true, path: { id }, body })
      ).then((r) => r.data);
    },
    onSuccess: () => invalidate(),
  });
}

/** Reconcile a dangling provider-side refund event against our own records. */
export function useRefundMatch() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({
      attemptId,
      providerRef,
      reason,
      operationId,
    }: {
      attemptId: string;
      providerRef?: string;
      reason?: string;
      operationId: string;
    }) =>
      matchFinanceRefundAttempt({
        throwOnError: true,
        path: { id: attemptId },
        body: { operation_id: operationId, provider_ref: providerRef, reason },
      }).then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/**
 * Attach an unpaired completed provider event to an existing open refund
 * obligation. `bookingId` (D-I4, final review): optional, like
 * useSettleScheduleEntry — the events screen's Attach dialog does not know
 * a booking (an unpaired attempt has no booking context there), but
 * RefundsCard does and passes its own.
 */
export function useRefundAttach(bookingId?: string) {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({
      refundId,
      attemptId,
      operationId,
    }: {
      refundId: string;
      attemptId: string;
      operationId: string;
    }) =>
      attachFinanceRefund({
        throwOnError: true,
        path: { id: refundId },
        body: { operation_id: operationId, attempt_id: attemptId },
      }).then((r) => r.data),
    onSuccess: () => invalidate(bookingId),
  });
}

export function useRefundAbandon() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ attemptId, reason, operationId }: { attemptId: string; reason: string; operationId: string }) =>
      abandonFinanceRefundAttempt({
        throwOnError: true,
        path: { id: attemptId },
        body: { operation_id: operationId, reason },
      }).then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/**
 * D-I4 (final review): `bookingId` optional, like useSettleScheduleEntry —
 * the events screen's Split (an attempt's own paired obligation, no booking
 * context there) omits it; RefundsCard (which DOES know bookingId) passes
 * it so the same page's Payments card (a DIFFERENT query,
 * ['booking', id, 'payment-schedule']) doesn't go stale after a split
 * rewrites the tree's schedule projections server-side.
 */
export function useRefundSplit(bookingId?: string) {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({
      refundId,
      partsMinor,
      operationId,
    }: {
      refundId: string;
      partsMinor: number[];
      operationId: string;
    }) =>
      splitFinanceRefund({
        throwOnError: true,
        path: { id: refundId },
        body: { operation_id: operationId, parts_minor: partsMinor },
      }).then((r) => r.data),
    onSuccess: () => invalidate(bookingId),
  });
}

/** `bookingId` optional — same reasoning as useRefundSplit (D-I4). */
export function useRefundWriteOff(bookingId?: string) {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ refundId, reason, operationId }: { refundId: string; reason: string; operationId: string }) =>
      writeOffFinanceRefund({
        throwOnError: true,
        path: { id: refundId },
        body: { operation_id: operationId, reason },
      }).then((r) => r.data),
    onSuccess: () => invalidate(bookingId),
  });
}

/**
 * C-M6 (final review, SPA half): close now takes an OPTIONAL
 * `{operation_id}` body (PART 1 — backward compatible for a caller that
 * omits it), honoured via `runOperation` so a retry with the SAME key
 * replays the stored success instead of a bodiless 409 period_closed. Same
 * operationId-is-a-caller-owned-param shape as useReopenPeriod, below.
 */
export function useClosePeriod() {
  const qc = useQueryClient();
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ month, operationId }: { month: string; operationId: string }) =>
      closeFinancePeriod({ throwOnError: true, path: { month }, body: { operation_id: operationId } }).then(
        (r) => r.data,
      ),
    // The close answers the closed period (with its snapshot): seed it so the
    // card flips to the stored summary at once, before the refetches race —
    // the preview's own refetch now answers 409 period_closed (spec §5.4).
    onSuccess: (closed, { month }) => {
      if (closed) qc.setQueryData(financePeriodQuery(month).queryKey, closed);
      invalidate();
    },
  });
}

/** Same operationId-is-a-param fix as useResolveProviderFact (fix round 1, item 3/T23-3). */
export function useReopenPeriod() {
  const invalidate = useInvalidateFinance();
  return useMutation({
    mutationFn: ({ month, reason, operationId }: { month: string; reason: string; operationId: string }) =>
      reopenFinancePeriod({
        throwOnError: true,
        path: { month },
        body: { operation_id: operationId, reason },
      }).then((r) => r.data),
    onSuccess: () => invalidate(),
  });
}

/**
 * Refund obligations for one booking (the booking card's "Refunds" section)
 * or one payment (the events screen's Attach picker) — fix round 1, item 1
 * (Ruling T23-1): GET /finance/refunds, added by Task 19's own fix round to
 * make this task's Attach flow (and the booking card's refund visibility)
 * actually possible.
 */
export const financeRefundsQuery = (f: { paymentId?: string; bookingId?: string }) =>
  queryOptions({
    queryKey: [...root, 'refunds', f.paymentId ?? '', f.bookingId ?? ''] as const,
    queryFn: async ({ signal }) =>
      (
        await listFinanceRefunds({
          throwOnError: true,
          signal,
          query: { payment_id: f.paymentId, booking_id: f.bookingId },
        })
      ).data,
    // Only fetch once at least one scope is known — the server 422s an
    // unscoped call, and a caller that has neither id yet (e.g. the booking
    // card before its own booking has loaded) should not fire it at all.
    enabled: !!(f.paymentId || f.bookingId),
    staleTime: 15_000,
  });

/**
 * A screen driving MANY independent finblock actions (the events screen's
 * per-row Resolve/Match/Attach/…, the queue's per-row Split/Move) needs one
 * stable operation_id PER TARGET, reused across a failed-then-retried
 * invocation of the SAME logical action and retired once that action
 * actually succeeds (fix round 1, item 3/T23-3 — the same contract
 * useOperationId gives a single-target component, generalised to "many
 * targets, one screen" via a plain keyed cache instead of one hook call per
 * row). `get(key)` mints once per key and returns the SAME id on every
 * later call for that key; `clear(key)` retires it (call from onSuccess) so
 * the NEXT, unrelated invocation for that same target starts fresh.
 */
export function useStableOperationIds() {
  const ref = useRef(new Map<string, string>());
  return {
    get: (key: string): string => {
      let id = ref.current.get(key);
      if (!id) {
        id = newOperationId();
        ref.current.set(key, id);
      }
      return id;
    },
    clear: (key: string): void => {
      ref.current.delete(key);
    },
  };
}

/**
 * Fix round 2 (Critical + Important, re-review of fix round 1): every
 * `useStableOperationIds`-based call site used `mutation.mutate(vars, {
 * onSuccess, onError })` — a PER-CALL callback. TanStack Query v5's
 * MutationObserver (the object backing one `useMutation()` hook) tracks
 * only ONE `#currentMutation` at a time: calling `.mutate()`/`.mutateAsync()`
 * again on the SAME hook instance (one hook shared by every row on a
 * screen) calls `#currentMutation.removeObserver(this)` on the PREVIOUS
 * mutation before it has necessarily settled (mutationObserver.js). That
 * previous mutation's `onSuccess`/`onError` are delivered via the
 * observer's own `#notify`, which fires from the mutation's own
 * `#observers.forEach(o => o.onMutationUpdate(...))` — and this observer is
 * no longer in that list. So if the operator fires a SECOND row's action
 * before the FIRST settles, the first row's `opIds.clear(key)` (its
 * per-call onSuccess) is silently dropped: the id is never retired, and a
 * later, unrelated action on that same row replays the stale id — the
 * server either silently replays the old stored result (same body) or
 * answers `operation_conflict` (changed body), and either way the operator
 * sees no error and the row can look permanently stuck.
 *
 * The fix does not touch the per-call options mechanism at all: it uses
 * `mutateAsync(vars)`'s OWN returned promise instead. That promise is
 * `Mutation.execute()`'s return value (mutation.js) — a plain async
 * function's result, resolved/rejected by its own try/catch, with NO
 * dependency on whether the observer is still attached. A later
 * `.mutate()`/`.mutateAsync()` call on the same hook cannot affect a
 * promise a PREVIOUS call already returned.
 *
 * On success: retires the target's cached id (`opIds.clear`) so the next,
 * unrelated action on the same target mints a fresh one.
 * On `operation_conflict` (the committed id was replayed with a different
 * body — typically a lost response followed by a retry with changed
 * input): the key is retired too (so the operator's next attempt gets a
 * fresh id instead of conflicting forever) and the finance section's
 * queries are invalidated (the screen may be showing data that changed
 * server-side under the failed request) — the caller shows a message that
 * says so, not a generic failure.
 * On any other error: the key is left alone (unchanged input safely
 * replays), and the caller shows the server's own message.
 */
/**
 * D-M4 (final review): the single-target dialogs (ConfirmReceiptDialog,
 * SettleProviderFactForm, AttachToObligationForm) mint ONE operation_id for
 * the dialog's life (no shared-hook retry-key dance — a fresh mount per
 * open already gives each attempt its own id) but used to show the RAW
 * server message on operation_conflict and never invalidate, leaving the
 * screen showing data the conflicting write had already moved under it.
 * Shared here so both those dialogs and settleStableOperation say the same
 * thing.
 */
export const OPERATION_CONFLICT_MESSAGE = 'The data changed under this action — refreshed. Try again.';

export async function settleStableOperation(
  mutateAsyncCall: Promise<unknown>,
  key: string,
  opIds: { clear: (key: string) => void },
  invalidate: () => void,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await mutateAsyncCall;
    opIds.clear(key);
    return { ok: true };
  } catch (err) {
    if (errorCode(err) === 'operation_conflict') {
      opIds.clear(key);
      invalidate();
      return { ok: false, message: OPERATION_CONFLICT_MESSAGE };
    }
    return { ok: false, message: errorMessage(err) };
  }
}
